/*
 * (Trello 卡 6aab90b1e1dc16fd9ba865c0, 辉哥 9/17 15:01 拍板)
 * extend DiscussionPage view, 给 PageStructure 传 hero 函数
 *
 * 模式:
 *   - vendor PageStructure 提供 Page-hero 容器, hero 函数返非空 → 显示 (vendor PageStructure.tsx L100)
 *     <div className="Page-hero">{(this.attrs.hero && this.attrs.hero()) || null}</div>
 *   - vendor DiscussionPage.hero() 默认返 <DiscussionHero /> (vendor DiscussionPage.tsx L137)
 *   - ziven-core 改造 hero() 让 heroImageUrl 存在时返 <DiscussionPageHero />
 *     (内部已含 .Page-hero-image-wrap + overlay + DiscussionHero 标题, 见 DiscussionPageHero.jsx)
 *   - heroImageUrl 不存在时返 null (辉哥 9/17 18:19 拍板, 9/18 11:19 重申 d/13 bug 反馈)
 *
 * 为什么 hero() 返 null 不丢标题 (辉哥 9/18 11:19 重申 d/13 反馈 + ziven-theme-aura r.19 已闭环):
 *   - 辉哥 9/17 18:54 反悔 18:19 SOP 拍板'hero() 返 null', 当时怕 vendor DiscussionHero 标题/badges 丢
 *   - 但 ziven-theme-aura r.19 (9/17 10:32, Trello 卡 6aab53a942d8bae84470f97b) 已经把标题搬到
 *     Post-main 内 (PostHeroTitle wrapper), .Page-hero 隐藏不影响标题显示
 *   - 9/18 11:19 d/13 反馈确认 bug 复现, 回到 18:19 SOP 拍板原方案
 *   - LESS .Page-hero:empty { display: none } 兜底已经写好 (less/forum.less L1745-1747),
 *     hero() 返 null 自动触发隐藏
 *
 * (辉哥 9/17 15:16 反馈 Bug 1): 之前有图时返回数组
 *   [<DiscussionPageHero />, <DiscussionHero />] 导致 DiscussionHero 渲染 2 次
 *   (DiscussionPageHero 内部已含 vendor DiscussionHero, 又在数组里返第二个)
 *   修法: 有图时只返 [<DiscussionPageHero />], DiscussionHero 由 DiscussionPageHero 内部渲染
 *     这样 16:9 图 + overlay + 标题/badges 全部在 .Page-hero-image-wrap 内, overlay 让深色文字清晰
 *
 * (Trello 卡 6aacae9e3c9db548f7bef594, 辉哥 9/18 11:22 派 Coder 修,
 *   d/13 bug 反馈重写 hero() 返回值, 回到 18:19 SOP):
 *   r.18d (commit 6267472) 没改 hero() 返回值, 无图时仍返 `original.call(this)` (= vendor <DiscussionHero />),
 *   vendor 容器非空, LESS .Page-hero:empty { display: none } 不命中, 容器仍然显示
 *   r.18e (辉哥 18:19 SOP 拍板) 改 hero() 返 null, 但被 r.18f (9/17 18:54 辉哥反悔) 改回 original.call(this)
 *   9/18 11:19 辉哥 d/13 反馈确认 bug: d/13 无头图, .Page-hero 仍显示 vendor DiscussionHero
 *   修法: hero() 无图时返 null, 触发 vendor `|| null` fallback → 容器 :empty → display:none
 *   标题由 ziven-theme-aura r.19 (9/17 10:32) Post-main PostHeroTitle 提供, 无副作用
 */

import { extend as _rawExtend, override as _rawOverride } from 'flarum/common/extend';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionPageHero from './components/DiscussionPageHero';
export default function extendDiscussionPage() {
  // (辉哥 9/17 16:47 派 Coder 修): webpack DCE 把 const helper globals 去掉,
  // 改用 function declaration 让 helper 名字在函数体内可用
  function safeExtend(targetOrClass, method, fn) {
    if (!window.__zivenCoreSafeExtend) {
      console.warn('[ziven-core] safeExtend helper not loaded, skipped:', method);
      return null;
    }
    return window.__zivenCoreSafeExtend(targetOrClass, method, fn);
  }
  function safeOverride(targetOrClass, method, fn) {
    if (!window.__zivenCoreSafeOverride) {
      console.warn('[ziven-core] safeOverride helper not loaded, skipped:', method);
      return null;
    }
    return window.__zivenCoreSafeOverride(targetOrClass, method, fn);
  }

  // r.18c 真因 #2 (Mavis 16:11 debug 第二轮):
  //   vendor override 模式: `object[method] = function(...args) { return newMethod.apply(this, [original.bind(this), ...args]); }`
  //   vendor 把 newMethod 的返回值**直接当 hero() 的返回值** — 不是新函数,是 vnode
  //   我之前写 `safeOverride(DiscussionPage, 'hero', function(_original) { return function() {...}; })` 返函数, 函数被 stringify 成 DOM 文本
  //
  //   修法: override callback 直接返 vnode, 不要 return function
  //     vendor 调用 hero() 时会调 newMethod, newMethod 接收 original 作为 first arg, 实际是新方法的返回值
  //     直接根据 heroImageUrl 判断返 <DiscussionPageHero /> 或 null
  safeOverride(DiscussionPage, 'hero', function (original) {
    const heroUrl = this.discussion.attribute('heroImageUrl');
    if (heroUrl) {
      // 有图: 图 + overlay + 标题/badges 都在 .Page-hero-image-wrap 内
      // DiscussionPageHero 内部已含 vendor DiscussionHero (不要再返第二个)
      return m(DiscussionPageHero, {
        discussion: this.discussion
      });
    }

    // 无图: 返 null 触发 vendor '|| null' fallback + .Page-hero:empty { display: none } (less/forum.less L1745-1747)
    // 辉哥 9/17 18:19 SOP 拍板原方案, 9/18 11:19 d/13 bug 反馈重申回到此方案
    // 辉哥 9/17 18:54 r.18f 反悔错的 (当时怕标题丢), 这次回滚
    // 标题已在 Post-main 内 (ziven-theme-aura r.19 搬走, PostHeroTitle), 隐藏 .Page-hero 不影响标题
    return null;
  });
}